<template>
  <div class="full-height">
    <Spinner v-if="loading" />

    <div v-else-if="localConfig && localConfig.campaignData" class="full-height">
      <Card class="full-height">
        <!-- Name, type -->
        <div class="row">
          <!-- Name -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.name')"
                :validation="['required']"
                ref="data_name"
                v-if="isCreate || isEdit"
                v-model="name" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.name')"
                :value="name"
                v-else />
            </div>
          </div>

          <!-- Campaign type selection -->
          <div class="slds-form-element slds-size--medium col">
            <CampaignTypeSelector
              ref="data_campaignType"
              v-if="isCreate && localConfig.campaignData.type.options.length > 1"
              v-model="campaignType"
              :options="localConfig.campaignData.type.options" />

            <OutputField :label="$t('campaignDesigner.campaignSetup.type.label')" type="custom" v-else>
              <span class="slds-swatch type-color-container" :style="`background:${campaignType.color ? campaignType.color: '#FFFFFF'};`" />
              <span>{{campaignType.label}}</span>
            </OutputField>
          </div>
        </div>

        <!-- Masterkey, Market -->
        <div class="row">
          <!-- Masterkey campaign -->
          <div class="slds-form-element slds-size--medium col" v-if="masterKeys">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.masterKey')"
              :options="masterKeys"
              ref="data_masterkey"
              searchable
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="masterKey" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.masterKey')"
              :value="masterKey"
              type="custom"
              v-else>

              <span v-if="masterKeys && masterKeys.length && masterKey">{{masterKey.length && masterKeys.find(mk => mk.value === masterKey).label}}</span>
            </OutputField>
          </div>

          <!-- Market -->
          <div class="slds-form-element slds-size--medium col">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.market')"
              :options="localConfig.campaignData.market.options"
              :validation="['required']"
              labels-prefix="markets"
              ref="data_market"
              v-if="(isCreate || isEdit)"
              v-model="market" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.market')"
              :options="localConfig.campaignData.market.options"
              :value="market"
              labels-prefix="markets"
              type="multipicklist"
              v-else />
          </div>
        </div>

        <!-- Start date, End date -->
        <div class="row">
          <!-- Start date -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.startDate')"
                :validation="[{name: 'is-date-before', param: endDate}, 'is-date-after-today', 'required']"
                ref="data_startDate"
                type="date"
                v-if="isCreate || isEdit"
                v-model="startDate" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.startDate')"
                :value="startDate"
                type="date"
                v-else />
            </div>
          </div>

          <!-- End date -->
          <div class="slds-form-element slds-size--medium col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.endDate')"
                :validation="['required', 'is-date-after-today']"
                ref="data_endDate"
                type="date"
                v-if="isCreate || isEdit"
                v-model="endDate" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.endDate')"
                :value="endDate"
                type="date"
                v-else />
            </div>
          </div>
        </div>

        <!-- Brand, Model -->
        <div class="row">
          <!-- Brand -->
          <div class="slds-form-element col slds-size--medium">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.brand')"
              :options="localConfig.campaignData.brand.options"
              :validation="['required']"
              labels-prefix="brands"
              ref="data_brand"
              v-if="(isCreate || isEdit)"
              v-model="brand" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.brand')"
              :options="localConfig.campaignData.brand.options"
              :value="brand"
              labels-prefix="brands"
              type="picklist"
              v-else />
          </div>

          <!-- Model -->
           <div class="slds-form-element col">
            <div class="slds-form-element col slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.model')"
                :validation="['required']"
                v-if="isCreate || isEdit"
                v-model="model" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.model')"
                :value="model"
                v-else />
            </div>
          </div>
        </div>

        <!-- Father, Model description -->
        <div class="row">
          <!-- Father -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Lookup
                :label="$t('campaignDesigner.campaignSetup.parent')"
                :mode="(isCreate || isEdit) ? 'edit': 'view'"
                :records="ddsCampaigns"
                base-url="/campaigns"
                record-type="campaign"
                v-model="parent"
                />
            </div>
          </div>

          <!-- Model description -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.modelDescription')"
                :validation="['required']"
                ref="data_modelDescription"
                v-if="isCreate || isEdit"
                v-model="modelDescription" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.modelDescription')"
                :value="modelDescription"
                v-else />
            </div>
          </div>
        </div>

        <!-- Acceptance expiring date, Score -->
        <div class="row">
          <div class="slds-form-element col">
            <!-- Acceptance start date -->
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.acceptanceExpiringDate')"
                :validation="['required', 'is-date-after-today']"
                ref="data_acceptanceExpiringDate"
                type="date"
                v-if="isCreate || isEdit"
                v-model="acceptanceExpiringDate" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.acceptanceExpiringDate')"
                :value="acceptanceExpiringDate"
                type="date"
                v-else />
            </div>
          </div>

          <!-- Score -->
          <div class="slds-form-element slds-size--medium col">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.score')"
              :options="localConfig.campaignData.score.options"
              ref="data_score"
              searchable
              v-if="isCreate || isEdit"
              v-model="score" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.score')"
              :options="localConfig.campaignData.score.options"
              :value="score"
              type="picklist"
              v-else />
          </div>
        </div>

        <!-- Channels, Codpack -->
        <div class="row">
          <!-- Channels -->
          <div class="slds-form-element col slds-size--medium">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.channels.label')"
              :options="localConfig.campaignData.campaignChannel.options"
              :validation="['required']"
              labels-prefix="channels"
              multipicklist
              ref="data_channels"
              v-if="isCreate || isEdit"
              v-model="campaignChannel" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.channels.label')"
              :options="localConfig.campaignData.campaignChannel.options"
              :value="campaignChannel"
              labels-prefix="channels"
              type="multipicklist"
              v-else />
          </div>

          <!-- Codpack -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.codpack')"
                :validation="[
                  {name: 'bigger-or-equal-than', param: 1 },
                  {name: 'less-or-equal-than', param: 9999 },
                ]"
                ref="data_codpack"
                type="number"
                v-if="isCreate || isEdit"
                v-model="codpack" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.codpack')"
                :value="codpack"
                v-else />
            </div>
          </div>
        </div>

        <!-- Finality, Dealer custom contacts -->
        <div class="row">
          <!-- Finality -->
          <div class="slds-form-element slds-size--medium col">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.finality.label')"
              :options="localConfig.campaignData.finality.options"
              labels-prefix="campaignDesigner.campaignSetup.finality"
              ref="data_finality"
              searchable
              v-if="isCreate || isEdit"
              v-model="finality" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.finality.label')"
              :options="localConfig.campaignData.finality.options"
              :value="finality"
              labels-prefix="campaignDesigner.campaignSetup.finality"
              type="picklist"
              v-else />
          </div>

          <!-- Dealer custom contacts -->
          <div class="slds-form-element col slds-size--medium">
            <Input
              :label="$t('campaignDesigner.campaignSetup.dealerCustomContacts')"
              type="checkbox"
              v-if="isCreate || isEdit"
              v-model="dealerCustomContacts" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.dealerCustomContacts')"
              :value="dealerCustomContacts"
              type="checkbox"
              v-else />
          </div>
        </div>

        <!-- Relevant Redemption, Segment Type -->
        <div class="row">
          <!-- Relevant Redemption -->
          <div class="slds-form-element col slds-size--medium">
            <OutputField
              :label="$t('campaignDesigner.campaignSetup.relevantRedemption')"
              labels-prefix="campaignDesigner.campaignSetup.segmentType"
              type="checkbox"
              value />
          </div>

          <!-- Segment Type -->
          <div class="slds-form-element slds-size--medium col">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.segmentType.label')"
              :options="localConfig.campaignData.segmentType.options"
              :validation="['required']"
              labels-prefix="campaignDesigner.campaignSetup.segmentType"
              ref="data_segmentType"
              v-if="isCreate || isEdit"
              v-model="segmentType" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.segmentType.label')"
              :options="localConfig.campaignData.segmentType.options"
              :value="segmentType"
              labels-prefix="campaignDesigner.campaignSetup.segmentType"
              type="picklist"
              v-else />
          </div>
        </div>

        <!-- CCDB Report Enabled, Campaign Pull Push -->
        <div class="row">
          <!-- CCDB Report Enabled -->
          <div class="slds-form-element col slds-size--medium">
            <Input
              :label="$t('campaignDesigner.campaignSetup.ccdbReportEnabled')"
              :tooltip="$t('campaignDesigner.campaignSetup.ccdbReportEnabledTooltip.dds.child')"
              tooltip-direction="right"
              type="checkbox"
              v-if="isCreate || isEdit"
              v-model="ccdbReportEnabled" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.ccdbReportEnabled')"
              :tooltip="$t('campaignDesigner.campaignSetup.ccdbReportEnabledTooltip.dds.child')"
              :value="ccdbReportEnabled"
              tooltip-direction="right"
              type="checkbox"
              v-else />

          </div>

          <!-- Campaign Pull Push -->
          <div class="slds-form-element slds-size--medium col">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.campaignPullPush.label')"
              :options="localConfig.campaignData.campaignPullPush.options"
              labels-prefix="campaignDesigner.campaignSetup.campaignPullPush"
              ref="data_campaignPullPush"
              searchable
              v-if="isCreate || isEdit"
              v-model="campaignPullPush" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.campaignPullPush.label')"
              :options="localConfig.campaignData.campaignPullPush.options"
              :value="campaignPullPush"
              labels-prefix="campaignDesigner.campaignSetup.campaignPullPush"
              type="picklist"
              v-else />
          </div>
        </div>

        <!-- Manager, External list -->
        <div class="row">
          <!-- Manager -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Picklist
                :label="$t('campaignDesigner.campaignSetup.manager.label')"
                :options="localConfig.campaignData.manager.options"
                labels-prefix="campaignDesigner.campaignSetup.manager"
                ref="data_manager"
                searchable
                v-if="isCreate || isEdit"
                v-model="manager" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.manager.label')"
              :options="localConfig.campaignData.manager.options"
              :value="manager"
              type="picklist"
              v-else />
            </div>
          </div>

          <!-- External list -->
          <div class="slds-form-element col slds-size--medium">
            <Input
              :label="$t('campaignDesigner.campaignSetup.externalList')"
              type="checkbox"
              v-if="isCreate || isEdit"
              v-model="externalList" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.externalList')"
              :value="externalList"
              type="checkbox"
              v-else />
          </div>
        </div>

        <!-- Model CPS, Media Campaign Id -->
        <div class="row">
          <!-- Model CPS -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.modelCps')"
                ref="data_modelCps"
                v-if="isCreate || isEdit"
                v-model="modelCps" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.modelCps')"
                :value="modelCps"
                v-else />
            </div>
          </div>

          <!-- Media Campaign Id -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.mediaCampaignId')"
                ref="data_mediaCampaignId"
                v-if="isCreate || isEdit"
                v-model="mediaCampaignId" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.mediaCampaignId')"
                :value="mediaCampaignId"
                v-else />
            </div>
          </div>
        </div>

        <!-- Image preview url, thumb preview url -->
        <div class="row">
          <!-- Image preview url -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.imagePreviewUrl')"
                :validation="['required', 'url']"
                ref="data_imagePreviewUrl"
                v-if="isCreate || isEdit"
                v-model="imagePreviewUrl" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.imagePreviewUrl')"
                :value="imagePreviewUrl"
                v-else />
            </div>
          </div>

          <!-- Thumb preview url -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.thumbPreviewUrl')"
                :validation="['required', 'url']"
                ref="data_thumbPreviewUrl"
                v-if="isCreate || isEdit"
                v-model="thumbPreviewUrl" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.thumbPreviewUrl')"
                :value="thumbPreviewUrl"
                v-else />
            </div>
          </div>
        </div>

        <!-- HTML Description -->
        <Card additional-classes="slds-card_boundary slds-m-top--medium" :title="$t('campaignDesigner.campaignSetup.htmlDescription')">
          <transition name="fade">
            <Alert type="error" :message="htmlDescriptionError" v-if="htmlDescriptionError" />
          </transition>
          <template #actions>
            <button class="slds-button slds-button_brand" @click="updateTemplate" v-if="isCreate || isEdit">
              <svg class="slds-button__icon slds-button__icon_left" aria-hidden="true">
                <use xlink:href="../../../../assets/symbols.svg#refresh"></use>
              </svg>
              {{ $t('campaignDesigner.campaignSetup.updatePreview') }}
            </button>
          </template>
          <div class="row">
            <div class="slds-form-element col">
              <div class="slds-form-element">
                <label class="slds-form-element__label">
                  <abbr class="slds-required" title="required" v-if="isCreate || isEdit">*</abbr>
                  {{$t('campaignDesigner.campaignSetup.htmlDescription')}} ({{(htmlDescription || '').length}} / 4000)
                </label>
                <ace-editor
                  @init="editorInit"
                  v-model="htmlDescription"
                  height="300"
                  lang="html"
                  theme="monokai"
                  ref="htmlTemplate"
                  width="100%"
                  :options="{readOnly: isView}" />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="slds-form-element col">
              <div class="slds-form-element">
                <label class="slds-form-element__label">{{$t('campaignDesigner.campaignSetup.htmlDescriptionPreview')}}</label>
                <div class="preview" v-html="updatedHtmlDescriptionPreview" />
              </div>
            </div>
          </div>
        </Card>

        <!-- Channel-specific fields -->
        <!-- Email -->
        <Card additional-classes="slds-card_boundary slds-m-top--medium" :title="$t('channels.email')" v-if="campaignChannel.includes('email')">
          <div class="row">
            <!-- Planned send date DEM -->
            <div class="slds-form-element slds-size--medium col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.plannedSendDateDem')"
                  :validation="['is-date-after-today']"
                  ref="data_plannedSendDateDem"
                  type="date"
                  v-if="isCreate || isEdit"
                  v-model="plannedSendDateDem" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.plannedSendDateDem')"
                  :value="plannedSendDateDem"
                  type="date"
                  v-else />
              </div>
            </div>

            <!-- DEM Offer -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.demOffer')"
                  ref="data_demOffer"
                  v-if="isCreate || isEdit"
                  v-model="demOffer" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.demOffer')"
                  :value="demOffer"
                  v-else />
              </div>
            </div>
          </div>

          <!-- DEM Tiny Url, DEM Tiny URL 1 -->
          <div class="row">
            <!-- DEM Tiny URL -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl')"
                  :validation="['url']"
                  ref="data_demTinyUrl"
                  v-if="isCreate || isEdit"
                  v-model="demTinyUrl" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl')"
                  :value="demTinyUrl"
                  v-else />
              </div>
            </div>

            <!-- DEM Tiny URL 1 -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl1')"
                  :validation="['url']"
                  ref="data_demTinyUrl1"
                  v-if="isCreate || isEdit"
                  v-model="demTinyUrl1" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl1')"
                  :value="demTinyUrl1"
                  v-else />
              </div>
            </div>
          </div>

          <!-- DEM Tiny Url 2, DEM Tiny URL 3 -->
          <div class="row">
            <!-- DEM Tiny URL 2 -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl2')"
                  :validation="['url']"
                  ref="data_demTinyUrl2"
                  v-if="isCreate || isEdit"
                  v-model="demTinyUrl2" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl2')"
                  :value="demTinyUrl2"
                  v-else />
              </div>
            </div>

            <!-- DEM Tiny URL 3 -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl3')"
                  :validation="['url']"
                  ref="data_demTinyUrl3"
                  v-if="isCreate || isEdit"
                  v-model="demTinyUrl3" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl3')"
                  :value="demTinyUrl3"
                  v-else />
              </div>
            </div>
          </div>

          <!-- DEM Tiny Url 4, DEM Tiny URL 3 -->
          <div class="row">
            <!-- DEM Tiny URL 4 -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl4')"
                  :validation="['url']"
                  ref="data_demTinyUrl4"
                  v-if="isCreate || isEdit"
                  v-model="demTinyUrl4" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl4')"
                  :value="demTinyUrl4"
                  v-else />
              </div>
            </div>

            <!-- DEM Tiny URL 5 -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl5')"
                  :validation="['url']"
                  ref="data_demTinyUrl5"
                  v-if="isCreate || isEdit"
                  v-model="demTinyUrl5" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.demTinyUrl5')"
                  :value="demTinyUrl5"
                  v-else />
              </div>
            </div>
          </div>
        </Card>

        <!-- SMS -->
        <Card additional-classes="slds-card_boundary slds-m-top--medium" :title="$t('channels.sms')" v-if="campaignChannel.includes('sms')">
          <div class="row">
            <!-- Planned send date SMS -->
            <div class="slds-form-element slds-size--medium col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.plannedSendDateSMS')"
                  :validation="['is-date-after-today']"
                  ref="data_plannedSendDateSMS"
                  type="date"
                  v-if="isCreate || isEdit"
                  v-model="plannedSendDateSMS" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.plannedSendDateSMS')"
                  :value="plannedSendDateSMS"
                  type="date"
                  v-else />
              </div>
            </div>

            <!-- SMS Tiny URL -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.smsTinyUrl')"
                  :validation="['url']"
                  ref="data_smsTinyUrl"
                  v-if="isCreate || isEdit"
                  v-model="smsTinyUrl" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.smsTinyUrl')"
                  :value="smsTinyUrl"
                  v-else />
              </div>
            </div>
          </div>
          <div class="row">
            <!-- SMS Offer -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.smsOffer')"
                  v-if="isCreate || isEdit"
                  v-model="smsOffer" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.smsOffer')"
                  :value="smsOffer"
                  v-else />
              </div>
            </div>
          </div>
        </Card>

        <!-- Paper -->
        <Card additional-classes="slds-card_boundary slds-m-top--medium" :title="$t('channels.document')" v-if="campaignChannel.includes('document')">
          <div class="row">
            <!-- DM Tiny URL -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.dmTinyUrl')"
                  :validation="['url']"
                  ref="data_dmTinyUrl"
                  v-if="isCreate || isEdit"
                  v-model="dmTinyUrl" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.dmTinyUrl')"
                  :value="dmTinyUrl"
                  v-else />
              </div>
            </div>

            <!-- DM QR Code -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.dmQRCode')"
                  :validation="['url']"
                  ref="data_dmQRCode"
                  v-if="isCreate || isEdit"
                  v-model="dmQRCode" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.dmQRCode')"
                  :value="dmQRCode"
                  v-else />
              </div>
            </div>
          </div>
          <div class="row">
            <!-- DM Offer -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.dmOffer')"
                  v-if="isCreate || isEdit"
                  v-model="dmOffer" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.dmOffer')"
                  :value="dmOffer"
                  v-else />
              </div>
            </div>

            <!-- Provider Elt -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Picklist
                  :label="$t('campaignDesigner.campaignSetup.providerElt')"
                  :options="localConfig.campaignData.providerElt.options"
                  ref="data_providerElt"
                  v-if="isCreate || isEdit"
                  v-model="providerElt" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.providerElt')"
                  :options="localConfig.campaignData.providerElt.options"
                  :value="providerElt"
                  type="picklist"
                  v-else />
              </div>
            </div>
          </div>
        </Card>

        <!-- Call -->
        <Card additional-classes="slds-card_boundary slds-m-top--medium" :title="$t('channels.call')" v-if="campaignChannel.includes('call')">
          <div class="row">
            <!-- Call Tiny URL -->
            <div class="slds-form-element col">
              <div class="slds-form-element slds-size--medium">
                <Input
                  :label="$t('campaignDesigner.campaignSetup.callTinyUrl')"
                  :validation="['url']"
                  ref="data_callTinyUrl"
                  v-if="isCreate || isEdit"
                  v-model="callTinyUrl" />

                <OutputField
                  :label="$t('campaignDesigner.campaignSetup.callTinyUrl')"
                  :value="callTinyUrl"
                  v-else />
              </div>
            </div>
          </div>
        </Card>
      </Card>
    </div>
  </div>
</template>
<script>
import AceEditor from 'vue2-ace-editor'
import Alert from '../../../slds/Alert'
import Card from '../../../slds/Card'
import Input from '../../../slds/Input'
import Lookup from '../../../slds/Lookup'
import OutputField from '../../../slds/OutputField'
import HasMode from '../../../utils/HasMode'
import Picklist from '../../../slds/Picklist'
import Spinner from '../../../Spinner'
import { keyBy, validate } from '../../../utils/utils'
import { getAllData } from '../../../utils/indexedDbUtils'
import moment from 'moment'
import { mapActions } from 'vuex'
export default {
  mixins: [HasMode],
  components: {
    AceEditor,
    Alert,
    Card,
    Input,
    Lookup,
    OutputField,
    Picklist,
    Spinner
  },
  props: {
    campaignStatus: String,
    loading: Boolean,
    mode: String
  },
  data () {
    return {
      segmentType: '',
      modelDescription: '',
      acceptanceExpiringDate: '',
      brand: '',
      callTinyUrl: '',
      campaignChannel: [],
      campaignType: '',
      campaignPullPush: '',
      ccdbReportEnabled: false,
      codpack: '',
      demOffer: '',
      demTinyUrl: '',
      demTinyUrl1: '',
      demTinyUrl2: '',
      demTinyUrl3: '',
      demTinyUrl4: '',
      demTinyUrl5: '',
      dmOffer: '',
      dmQRCode: '',
      dmTinyUrl: '',
      ddsCampaigns: [],
      dealerCustomContacts: false,
      endDate: '',
      externalList: false,
      finality: '',
      hierarchical: false,
      htmlDescription: '',
      htmlDescriptionError: '',
      imagePreviewUrl: '',
      localConfig: {},
      manager: '',
      market: '',
      masterKey: '',
      masterKeys: [],
      mediaCampaignId: '',
      model: '',
      models: [],
      modelCps: '',
      modelDescription: '',
      modelsByBrand: {},
      name: '',
      parent: '',
      plannedSendDateDem: '',
      plannedSendDateSMS: '',
      providerElt: '',
      recordType: '',
      score: '',
      smsOffer: '',
      smsTinyUrl: '',
      startDate: '',
      thumbPreviewUrl: '',
      updatedHtmlDescriptionPreview: ''
    }
  },
  async created () {
    if (this.$route.query.type) {
      this.recordType = this.$route.query.type
    }

    const ddsFilters = {
      type: 'sl_dds',
      hierarchical: true,
      hasParent: false,
      status: ['Draft', 'Active']
    }

    const ddsCampaigns = await this.getCampaigns(ddsFilters)
    this.ddsCampaigns = ddsCampaigns.map(campaign => {
      return {
        id: campaign.id,
        name: `${campaign.id} - ${campaign.name}`,
        description: [campaign.status, campaign.masterKey]
      }
    })

    await this.callLoadFromStore()
    if (this.htmlDescription) {
      this.updateTemplate()
    }

    this.$emit('endLoading')
  },
  beforeCreate () {
    this.$emit('startLoading')
  },
  methods: {
    async callLoadFromStore () {
      const campaign = this.loadFromStore('campaignData', ['loading', 'oldConfig'])
      this.localConfig = this.$store.getters['campaignDesigner/state'].currentCampaignConfig

      this.masterKeys = (await this.$store.dispatch('settings/getSettings')).masterKeys

      if (this.localConfig.campaignData.type.options.length === 1) {
        const selectedType = this.localConfig.campaignData.type.options[0]
        this.campaignType = {
          label: selectedType.name,
          value: selectedType.code,
          color: selectedType.color,
        }
      }
    },
    editorInit () {
      require('brace/ext/language_tools')
      require('brace/mode/html')
      require('brace/theme/monokai')
      require('brace/snippets/html')
    },
    getSectionToSave () {
      const campaignType = {
        options: this.localConfig.campaignData.type.options,
        selected: this.localConfig.campaignData.type.options.find(type => type.code === this.campaignType.value)
      }

      const sectionToSave = {
        flat: {
          brands: this.brand,
          ccdbReportEnabled: this.ccdbReportEnabled,
          contributionType: 'Standard',
          endDate: moment(this.endDate).format('YYYY-MM-DD'),
          finality: this.finality,
          hierarchical: !!this.parent,
          manager: this.manager,
          marketCodes: this.market,
          masterKey: this.masterKey,
          name: this.name,
          parent: this.parent,
          recordType: this.recordType,
          startDate: moment(this.startDate).format('YYYY-MM-DD'),
          type: this.campaignType.value,
        },
        config: {
          acceptanceExpiringDate: {
            selected: moment(this.acceptanceExpiringDate).format('YYYY-MM-DD')
          },
          brand: {
            options: this.localConfig.campaignData.brand.options,
            selected: this.brand
          },
          callTinyUrl: {
            selected: this.callTinyUrl
          },
          ccdbReportEnabled: {
            options: this.localConfig.campaignData.ccdbReportEnabled.options,
            selected: this.ccdbReportEnabled
          },
          campaignChannel: {
            options: this.localConfig.campaignData.campaignChannel.options,
            selected: this.campaignChannel
          },
          campaignPullPush: {
            options: this.localConfig.campaignData.campaignPullPush.options,
            selected: this.campaignPullPush
          },
          codpack: {
            selected: this.codpack
          },
          dealerCustomContacts: {
            selected: this.dealerCustomContacts
          },
          demOffer: {
            selected: this.demOffer
          },
          demTinyUrl: {
            selected: this.demTinyUrl
          },
          demTinyUrl1: {
            selected: this.demTinyUrl1
          },
          demTinyUrl2: {
            selected: this.demTinyUrl2
          },
          demTinyUrl3: {
            selected: this.demTinyUrl3
          },
          demTinyUrl4: {
            selected: this.demTinyUrl4
          },
          demTinyUrl5: {
            selected: this.demTinyUrl5
          },
          dmOffer: {
            selected: this.dmOffer
          },
          dmQRCode: {
            selected: this.dmQRCode
          },
          dmTinyUrl: {
            selected: this.dmTinyUrl
          },
          externalList: {
            options: this.localConfig.campaignData.externalList.options,
            selected: this.externalList
          },
          finality: {
            options: this.localConfig.campaignData.finality.options,
            selected: this.finality
          },
          htmlDescription: {
            selected: this.htmlDescription || ''
          },
          imagePreviewUrl: {
            selected: this.imagePreviewUrl
          },
          manager: {
            options: this.localConfig.campaignData.manager.options,
            selected: this.manager
          },
          market: {
            options: this.localConfig.campaignData.market.options,
            selected: this.market
          },
          masterKey: {
            selected: this.masterKey
          },
          mediaCampaignId: {
            selected: this.mediaCampaignId
          },
          model: {
            selected: this.model
          },
          modelCps: {
            selected: this.modelCps
          },
          modelDescription: {
            selected: this.modelDescription
          },
          plannedSendDateDem: {
            selected: this.plannedSendDateDem
          },
          plannedSendDateSMS: {
            selected: this.plannedSendDateSMS
          },
          providerElt: {
            options: this.localConfig.campaignData.providerElt.options,
            selected: this.providerElt
          },
          score: {
            options: this.localConfig.campaignData.score.options,
            selected: this.score
          },
          segmentType: {
            options: this.localConfig.campaignData.segmentType.options,
            selected: this.segmentType
          },
          smsOffer: {
            selected: this.smsOffer
          },
          smsTinyUrl: {
            selected: this.smsTinyUrl
          },
          thumbPreviewUrl: {
            selected: this.thumbPreviewUrl
          },
          type: {
            options: this.localConfig.campaignData.type.options,
            selected: this.localConfig.campaignData.type.options.find(type => type.code === this.campaignType.value)
          }
        }
      }

      return sectionToSave
    },
    ...mapActions({
      getCampaigns: 'campaignDesigner/getCampaigns'
    }),
    updateTemplate () {
      this.updatedHtmlDescriptionPreview = this.htmlDescription
      this.$forceUpdate()
    },
    validate () {
      if (this.isCreate || this.isEdit) {
        this.htmlDescriptionError = ''
        let previewIsValid = true
        const htmlRef = this.$refs.htmlTemplate

        if (!htmlRef.value || htmlRef.value.length === 0) {
          this.htmlDescriptionError = this.$t('validation.required')
          previewIsValid = false
        }

        if (previewIsValid && htmlRef.value && htmlRef.value.length > 4000) {
          this.htmlDescriptionError = this.$t('validation.maxLength', { maxValue: 4000 })
          previewIsValid = false
        }

        const fieldsAreValid = validate(this, 'data', true)

        return previewIsValid && fieldsAreValid
      } else {
        const dateFrom = moment(this.startDate, 'YYYY-MM-DD')
        if ('Draft' === this.campaignStatus && dateFrom.isBefore(moment(), 'day')) {
          this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('campaignDesigner.campaignSetup.validation.startDateMustBeInFuture'))
        } else {
          return true
        }
      }
    },
  },
  computed: {
    channelsJSON () {
      return JSON.stringify(this.campaignChannel)
    }
  },
  watch: {
    channelsJSON (newValueJSON, oldValueJSON) {
      const newValue = JSON.parse(newValueJSON)
      const oldValue = JSON.parse(oldValueJSON)
      if (oldValue.includes('call') && !newValue.includes('call')) {
        this.callTinyUrl = ''
      }

      if (!oldValue.includes('document') && newValue.includes('document')) {
        if (this.localConfig.campaignData.providerElt.options.length === 1) {
          this.providerElt = this.localConfig.campaignData.providerElt.options[0]
        }
      }

      if (oldValue.includes('document') && !newValue.includes('document')) {
        this.dmTinyUrl = ''
        this.dmQRCode = ''
        this.dmOffer = ''
        this.providerElt = ''
      }
      if (oldValue.includes('email') && !newValue.includes('email')) {
        this.plannedSendDateDem = ''
        this.demOffer = ''
        this.demTinyUrl = ''
        this.demTinyUrl1 = ''
        this.demTinyUrl2 = ''
        this.demTinyUrl3 = ''
        this.demTinyUrl4 = ''
        this.demTinyUrl5 = ''
      }
      if (oldValue.includes('sms') && !newValue.includes('sms')) {
        this.plannedSendDateSMS = ''
        this.smsTinyUrl = ''
        this.smsOffer = ''
      }
    }
  }
}
</script>
<style lang="scss" scoped>
  .full-height {
    height: 100%;
    overflow-y: auto;
  }

  .row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-start;
  }

  .col {
    flex-grow: 1;
  }

  .type-color-container {
    margin-right: 10px;
  }

  .preview {
    background-color: white;
    border: 1px solid #dddbda;
    border-radius: 0.25rem;
    overflow-x: auto;
    height: 300px;
  }
</style>
